import React, { useEffect, useState } from 'react';
import { AppError } from 'ozira-app-core';
import { Card, Badge, Loading, ErrorState, EmptyState } from '../components/ui';
import { useSession } from '../context/SessionContext';
import { apiClient } from '../lib/apiClient';

interface SignalsResult {
  scope_type: string;
  scope_id: string;
  count: number;
  signals: Array<{ id: string; title: string | null; signal_type: string; status: string }>;
}

// Smallest useful OZIRA Home (spec section 51): current user, current
// organization/personal scope, and a real read from the one safe backend
// capability -- GET /api/intelligence/signals.
export function HomeScreen() {
  const { email, organization } = useSession();
  const [signals, setSignals] = useState<SignalsResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .call<SignalsResult>('/api/intelligence/signals')
      .then(({ data }) => { if (!cancelled) setSignals(data); })
      .catch((err) => { if (!cancelled) setError(err instanceof AppError ? err : new AppError('UNKNOWN')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--ozira-space-lg)', padding: 'var(--ozira-space-lg)' }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ color: 'var(--ozira-color-muted)', fontSize: 'var(--ozira-font-size-sm)' }}>Signed in as</div>
            <div style={{ fontWeight: 600 }}>{email ?? 'Unknown'}</div>
          </div>
          <Badge>
            {organization.state === 'ORGANIZATION' ? 'Organization' : organization.state === 'PERSONAL' ? 'Personal' : 'Account'}
          </Badge>
        </div>
      </Card>

      <Card>
        <h2 style={{ marginTop: 0, fontSize: 'var(--ozira-font-size-lg)' }}>Recent intelligence</h2>
        {loading ? <Loading label="Loading signals…" /> : null}
        {!loading && error ? <ErrorState title="Could not load signals" description="Try again in a moment." /> : null}
        {!loading && !error && signals && signals.count === 0 ? (
          <EmptyState title="No signals yet" description="Signals ingested for this scope will appear here." />
        ) : null}
        {!loading && !error && signals && signals.count > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--ozira-space-sm)' }}>
            {signals.signals.map((s) => (
              <li key={s.id} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{s.title ?? s.signal_type}</span>
                <Badge>{s.status}</Badge>
              </li>
            ))}
          </ul>
        ) : null}
      </Card>
    </div>
  );
}
